import React from 'react';
import { useWatch } from 'react-hook-form';
import {
    DefaultEditorOptions,
    ListButtons,
    RichTextInput,
    RichTextInputToolbar,
    useTiptapEditor,
} from 'ra-input-rich-text';
import { useRecordContext } from 'react-admin';
import { Box, ToggleButton } from '@mui/material';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import Placeholder from '@tiptap/extension-placeholder';
import { BODY_MAX_VISIBLE_LENGTH, LONG_AUTO_TRANSLATION_WARNING, isLongAutoTranslation, visibleLength } from '../common';
import { FieldLabel, HelpText } from './CharCounter';
import LinkButton from './LinkButton';
import { MaxVisibleLength } from './maxVisibleLength';
import { LINK_PASTE_HINT_EVENT, PasteLink } from './pasteLink';

export const BODY_PLACEHOLDER = 'Explica en pocas frases qué cambia y dónde encontrarlo.';
export const LINK_HELP = 'Para añadir un enlace, selecciona un texto y pega la URL encima, o pulsa el botón de enlace.';

const extension = (name) => DefaultEditorOptions.extensions.find((ext) => ext.name === name);

/**
 * Solo las marcas que sobreviven a la lista blanca de D-14 (`p, br, strong, b, em, i, u, ul, ol,
 * li, a[href]`): sin encabezados, citas, código, tachado ni imágenes, para que lo que se ve al
 * escribir sea lo que el back guarda.
 */
export const newsEditorOptions = {
    ...DefaultEditorOptions,
    extensions: [
        extension('starterKit').configure({
            heading: false,
            blockquote: false,
            code: false,
            codeBlock: false,
            horizontalRule: false,
            strike: false,
        }),
        extension('underline'),
        extension('link').configure({
            openOnClick: false,
            // El `handlePaste` de @tiptap/extension-link 2.1 inserta en `selection.from - 1`: con todo
            // seleccionado (Ctrl+A, selección desde la posición 0) revienta con «RangeError: Position
            // -1 out of range», y con otras selecciones mete el contenido un nivel fuera del
            // párrafo. Sin él, una URL sola la pega PasteLink y el resto ProseMirror (recortado por
            // MaxVisibleLength).
            linkOnPaste: false,
            validate: (href) => /^(https?:|mailto:)/i.test(href),
        }),
        Placeholder.configure({ placeholder: BODY_PLACEHOLDER }),
        MaxVisibleLength.configure({ limit: BODY_MAX_VISIBLE_LENGTH }),
        PasteLink.configure({ limit: BODY_MAX_VISIBLE_LENGTH }),
    ],
};

const FORMATS = [
    { value: 'bold', label: 'Negrita', Icon: FormatBold, toggle: (editor) => editor.chain().focus().toggleBold().run() },
    { value: 'italic', label: 'Cursiva', Icon: FormatItalic, toggle: (editor) => editor.chain().focus().toggleItalic().run() },
    { value: 'underline', label: 'Subrayado', Icon: FormatUnderlined, toggle: (editor) => editor.chain().focus().toggleUnderline().run() },
];

// `FormatButtons` de ra-input-rich-text trae también tachado y código, que el back descarta.
const BasicFormatButtons = ({ size }) => {
    const editor = useTiptapEditor();
    const [, forceRender] = React.useReducer((n) => n + 1, 0);

    React.useEffect(() => {
        if (!editor) return undefined;
        editor.on('transaction', forceRender);
        return () => editor.off('transaction', forceRender);
    }, [editor]);

    return FORMATS.map(({ value, label, Icon, toggle }) => (
        <ToggleButton
            key={value}
            value={value}
            size={size}
            aria-label={label}
            title={label}
            selected={!!editor?.isActive(value)}
            disabled={!editor?.isEditable}
            onChange={() => toggle(editor)}
        >
            <Icon fontSize="inherit" />
        </ToggleButton>
    ));
};

// Va dentro de la barra porque es lo único que RichTextInput monta con el editor en contexto.
const LinkPasteHintListener = ({ onHint }) => {
    const editor = useTiptapEditor();

    React.useEffect(() => {
        if (!editor) return undefined;
        const clear = () => onHint(null);
        editor.on(LINK_PASTE_HINT_EVENT, onHint);
        editor.on('update', clear);
        return () => {
            editor.off(LINK_PASTE_HINT_EVENT, onHint);
            editor.off('update', clear);
        };
    }, [editor, onHint]);

    return null;
};

const NewsToolbar = ({ onLinkPasteHint }) => (
    <RichTextInputToolbar size="small">
        <BasicFormatButtons size="small" />
        <ListButtons size="small" />
        <LinkButton size="small" />
        <LinkPasteHintListener onHint={onLinkPasteHint} />
    </RichTextInputToolbar>
);

/** Texto enriquecido de una novedad con contador de caracteres visibles junto a la etiqueta. */
const RichBodyInput = ({ source, label }) => {
    const value = useWatch({ name: source });
    const record = useRecordContext();
    // `source` es `body.<idioma>` dentro de `TranslatableInputs`.
    const locale = source.split('.').pop();
    const title = useWatch({ name: `title.${locale}` });
    const longAuto = isLongAutoTranslation(record, { title: { [locale]: title }, body: { [locale]: value } }, locale);
    const [linkPasteHint, setLinkPasteHint] = React.useState(null);

    return (
        <Box>
            <FieldLabel htmlFor={source} label={label} count={visibleLength(value)} max={BODY_MAX_VISIBLE_LENGTH} warning={longAuto} />
            <RichTextInput
                source={source}
                label={false}
                fullWidth
                editorOptions={newsEditorOptions}
                toolbar={<NewsToolbar onLinkPasteHint={setLinkPasteHint} />}
                sx={(theme) => ({
                    '& .ProseMirror': {
                        minHeight: 120,
                        backgroundColor: `${theme.palette.background.paper} !important`,
                        borderRadius: 2,
                        p: 1.5,
                    },
                    '& .ProseMirror p.is-editor-empty:first-of-type::before': {
                        content: 'attr(data-placeholder)',
                        color: theme.palette.text.disabled,
                        float: 'left',
                        height: 0,
                        pointerEvents: 'none',
                    },
                })}
            />
            {linkPasteHint ? (
                <HelpText color="warning.dark" data-testid="link-paste-hint" role="status" aria-live="polite">
                    {linkPasteHint}
                </HelpText>
            ) : (
                <HelpText>{LINK_HELP}</HelpText>
            )}
            {longAuto && (
                <HelpText color="warning.dark" data-testid="long-auto-translation">
                    {LONG_AUTO_TRANSLATION_WARNING}
                </HelpText>
            )}
        </Box>
    );
};

export default RichBodyInput;
